import { hexToLottieRGBA } from 'utils/lottie';

type LottieRGBA = [number, number, number, number];
type LottieRGB = [number, number, number];

export type LottieThemeTokenValue = string | string[];
export type LottieThemeTokenMap = Record<string, LottieThemeTokenValue>;

type LottieKeyframe<T> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  o?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  i?: any;
  t?: number;
  s?: T;
};

type LottieAnimated<T> = {
  a?: 0 | 1;
  k?: T | LottieKeyframe<T>[];
};

type LottieFillStroke = {
  c?: LottieAnimated<LottieRGB | LottieRGBA>;
};

type LottieGradient = {
  p?: number;
  k?: LottieAnimated<number[]>;
};

type LottieGradientFillStroke = {
  g?: LottieGradient;
};

const deepClone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const formatColorToExistingLength = (
  existing: LottieRGB | LottieRGBA,
  rgba: LottieRGBA,
): LottieRGB | LottieRGBA => {
  if (existing.length === 3) {
    return [rgba[0], rgba[1], rgba[2]];
  }

  return rgba;
};

const getTokenAtIndex = (
  token: LottieThemeTokenValue,
  index: number,
): string => {
  if (Array.isArray(token)) {
    return token[index % token.length];
  }

  return token;
};

const applyTokenToFillStroke = (
  node: LottieFillStroke,
  token: LottieThemeTokenValue,
) => {
  const animated = node.c;
  if (!animated || animated.k == null) {
    return;
  }

  const k = animated.k as unknown;

  // Static colour: c.k = [r,g,b,a] (or [r,g,b])
  if (Array.isArray(k) && typeof k[0] === 'number') {
    const existing = k as LottieRGB | LottieRGBA;
    const rgba = hexToLottieRGBA(getTokenAtIndex(token, 0));
    animated.k = formatColorToExistingLength(existing, rgba);

    return;
  }

  // Animated colour: c.k = [{ s: [r,g,b], t: ... }, ...]
  if (Array.isArray(k)) {
    (k as LottieKeyframe<LottieRGB | LottieRGBA>[]).forEach((kf, idx) => {
      if (!kf.s) {
        return;
      }

      const rgba = hexToLottieRGBA(getTokenAtIndex(token, idx));
      kf.s = formatColorToExistingLength(kf.s, rgba);
    });
  }
};

const applyTokenToGradient = (
  node: LottieGradientFillStroke,
  token: LottieThemeTokenValue,
) => {
  const gradient = node.g;
  if (!gradient || !gradient.k || gradient.p == null || gradient.k.k == null) {
    return;
  }

  const p = gradient.p;

  // We expect `token` to supply `p` colours (or more).
  const colors: string[] = Array.isArray(token) ? token : [token];
  if (colors.length < p) {
    return;
  }

  const applyStops = (stops: number[]) => {
    // Stops are [pos, r, g, b] repeated p times.
    // We preserve the existing positions, replace the RGBs.
    for (let i = 0; i < p; i += 1) {
      const [r, g, b] = hexToLottieRGBA(colors[i]);
      const base = i * 4 + 1;
      if (stops.length >= base + 2) {
        stops[base] = r;
        stops[base + 1] = g;
        stops[base + 2] = b;
      }
    }
  };

  const k = gradient.k.k as unknown;

  // Static stops array
  if (Array.isArray(k) && typeof k[0] === 'number') {
    applyStops(k as number[]);

    return;
  }

  // Animated stops array
  if (Array.isArray(k)) {
    (k as LottieKeyframe<number[]>[]).forEach(kf => {
      if (!kf.s) {
        return;
      }

      applyStops(kf.s);
    });
  }
};

const traverseAndApplyThemeTokens = (
  node: unknown,
  tokens: LottieThemeTokenMap,
) => {
  if (!node) return;

  if (Array.isArray(node)) {
    node.forEach(child => traverseAndApplyThemeTokens(child, tokens));

    return;
  }

  if (typeof node !== 'object') {
    return;
  }

  const obj = node as Record<string, unknown>;

  const nm = obj.nm;
  if (typeof nm === 'string' && nm in tokens) {
    const token = tokens[nm];
    applyTokenToFillStroke(obj as unknown as LottieFillStroke, token);
    applyTokenToGradient(obj as unknown as LottieGradientFillStroke, token);
  }

  Object.values(obj).forEach(child =>
    traverseAndApplyThemeTokens(child, tokens),
  );
};

export const applyThemeToLottie = <T extends object>(
  animationData: T,
  tokens?: LottieThemeTokenMap,
): T => {
  if (!tokens || Object.keys(tokens).length === 0) {
    return animationData;
  }

  const cloned = deepClone(animationData);
  traverseAndApplyThemeTokens(cloned, tokens);

  return cloned;
};
